import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Shield, Lock, MessageSquare, Mail, Phone, Calendar, Building2 } from "lucide-react";
import { toast } from "sonner";
import usePageTitle from "@/hooks/usePageTitle";

const CATEGORY_COLORS = {
  feature_request: "bg-blue-500/10 text-blue-400 border-blue-500/20",
  bug_report: "bg-red-500/10 text-red-400 border-red-500/20",
  improvement: "bg-purple-500/10 text-purple-400 border-purple-500/20",
  question: "bg-yellow-500/10 text-yellow-400 border-yellow-500/20",
  other: "bg-slate-500/10 text-slate-400 border-slate-500/20"
};

const CATEGORY_LABELS = {
  feature_request: "✨ Feature Request",
  bug_report: "🐛 Bug Report",
  improvement: "🚀 Improvement",
  question: "❓ Question",
  other: "💬 Other"
};

const AdminFeedbackDashboard = () => {
  usePageTitle("pageTitles.customerVoice");
  const [authenticated, setAuthenticated] = useState(false);
  const [adminKey, setAdminKey] = useState("");
  const [feedbacks, setFeedbacks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("all");

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.get(`${API}/admin/feedback`, {
        headers: {
          "X-Admin-Key": adminKey
        }
      });
      
      setFeedbacks(response.data);
      setAuthenticated(true);
      toast.success("Authenticated successfully");
    } catch (error) {
      console.error("Authentication failed:", error);
      toast.error("Invalid admin key");
    } finally {
      setLoading(false);
    }
  };

  const refreshFeedbacks = async () => {
    if (!authenticated) return;
    
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/feedback`, {
        headers: {
          "X-Admin-Key": adminKey
        }
      });
      setFeedbacks(response.data);
      toast.success("Feedback list refreshed");
    } catch (error) {
      console.error("Error refreshing feedback:", error);
      toast.error("Failed to refresh feedback");
    } finally {
      setLoading(false);
    }
  };

  const filteredFeedbacks = filter === "all"
    ? feedbacks
    : feedbacks.filter(f => f.category === filter);

  if (!authenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 flex items-center justify-center p-8">
        <div className="max-w-md w-full bg-slate-900/50 border border-slate-800 rounded-lg p-8">
          <div className="flex items-center justify-center mb-6">
            <Shield className="h-12 w-12 text-amber-500" />
          </div>
          <h1 className="text-2xl font-bold text-white text-center mb-2">
            Admin Access
          </h1>
          <p className="text-slate-400 text-center mb-6">
            Enter admin key to view feedback dashboard
          </p>

          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <Label htmlFor="adminKey" className="text-slate-300 mb-2 block">
                Admin Key
              </Label>
              <Input
                id="adminKey"
                type="password"
                value={adminKey}
                onChange={(e) => setAdminKey(e.target.value)}
                placeholder="Enter admin key..."
                className="bg-slate-800 border-slate-700 text-white"
                required
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-amber-500 hover:bg-amber-600 text-slate-950 font-semibold"
            >
              {loading ? "Authenticating..." : (
                <span className="flex items-center gap-2">
                  <Lock className="h-4 w-4" />
                  Access Dashboard
                </span>
              )}
            </Button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-4xl font-bold text-white font-manrope mb-2">
              Feedback Dashboard
            </h1>
            <p className="text-slate-400">
              {filteredFeedbacks.length} feedback{filteredFeedbacks.length !== 1 ? "s" : ""}
            </p>
          </div>
          <Button
            onClick={refreshFeedbacks}
            disabled={loading}
            className="bg-amber-500 hover:bg-amber-600 text-slate-950 font-semibold"
          >
            {loading ? "Refreshing..." : "Refresh"}
          </Button>
        </div>

        {/* Filter Tabs */}
        <div className="flex gap-2 mb-6 flex-wrap">
          {["all", "feature_request", "bug_report", "improvement", "question", "other"].map((cat) => (
            <button
              key={cat}
              onClick={() => setFilter(cat)}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                filter === cat
                  ? "bg-amber-500 text-slate-950"
                  : "bg-slate-800 text-slate-300 hover:bg-slate-700"
              }`}
            >
              {cat === "all" ? "All" : CATEGORY_LABELS[cat]}
            </button>
          ))}
        </div>

        {/* Feedback List */}
        {filteredFeedbacks.length === 0 ? (
          <div className="bg-slate-900/50 border border-slate-800 rounded-lg p-12 text-center">
            <MessageSquare className="h-16 w-16 text-slate-600 mx-auto mb-4" />
            <p className="text-slate-400 text-lg">No feedback found</p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredFeedbacks.map((feedback) => (
              <div
                key={feedback.id}
                className="bg-slate-900/50 border border-slate-800 rounded-lg p-6 hover:border-slate-700 transition-colors"
              >
                {/* Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium border ${CATEGORY_COLORS[feedback.category]}`}>
                      {CATEGORY_LABELS[feedback.category]}
                    </span>
                    {feedback.department && (
                      <span className="px-3 py-1 rounded-full text-xs font-medium bg-amber-500/10 text-amber-400 border border-amber-500/20 flex items-center gap-1">
                        <Building2 className="h-3 w-3" />
                        {feedback.department}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 text-xs text-slate-500">
                    <Calendar className="h-3 w-3" />
                    {new Date(feedback.created_at).toLocaleString()}
                  </div>
                </div>

                {/* Message */}
                <div className="mb-4">
                  <p className="text-slate-200 whitespace-pre-wrap">{feedback.message}</p>
                </div>

                {/* Contact Info */}
                <div className="flex items-center gap-6 text-sm text-slate-400 pt-4 border-t border-slate-800">
                  <div className="flex items-center gap-2">
                    <Mail className="h-4 w-4 text-amber-500" />
                    {feedback.user_email}
                  </div>
                  {feedback.phone_number && (
                    <div className="flex items-center gap-2">
                      <Phone className="h-4 w-4 text-amber-500" />
                      {feedback.phone_number}
                    </div>
                  )}
                  <div className="text-slate-600 text-xs ml-auto">
                    ID: {feedback.id}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminFeedbackDashboard;
